/** Содержимое выдвижной панели для прокладки кабелей. */
import { useState } from "react";
import {
	CATALOG,
	cableItemById,
	ElectricalId,
	getCatalogItem,
	isJunctionBox,
	isPanel,
	isWiringHub,
} from "@/shared/catalog";
import { CatalogSectionKind, wiredEndIds } from "@/shared/plan";
import { useProjectStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { Tool, useEditorStore } from "@/widgets/editor";
import { groupWiringWarnings, wiringWarnings } from "@/widgets/electrical";
import { SelectionKind } from "@/shared/store";
import { WiringGroups } from "./WiringGroups";
import { WiringAutoDialog } from "./WiringAutoDialog";

export const WiringDrawer = () => {
	const [autoOpen, setAutoOpen] = useState(false);
	const tool = useEditorStore((s) => s.tool);
	const setTool = useEditorStore((s) => s.setTool);
	const setPlacing = useEditorStore((s) => s.setPlacing);
	const setSelection = useEditorStore((s) => s.setSelection);
	const cableMaterial = useEditorStore((s) => s.cableMaterial);
	const setCableMaterial = useEditorStore((s) => s.setCableMaterial);
	const electricalLayer = useProjectStore((s) => s.project.layers.electrical);
	const wiringLayer = useProjectStore((s) => s.project.layers.wiring);
	const layers = useProjectStore((s) => s.project.layers);
	const warnings = groupWiringWarnings(wiringWarnings(layers));
	const electricalFixtures = Object.values(electricalLayer.fixtures);
	const boxes = [...electricalFixtures, ...Object.values(electricalLayer.items)].filter((fixture) =>
		isJunctionBox(getCatalogItem(fixture.ref)),
	);
	const panels = electricalFixtures.filter((fixture) => isPanel(getCatalogItem(fixture.ref)));
	const wired = wiredEndIds(wiringLayer.items);
	const unconnected =
		electricalFixtures.filter(
			(fixture) => !isWiringHub(getCatalogItem(fixture.ref)) && !wired.has(fixture.id),
		).length +
		Object.values(electricalLayer.items).filter(
			(light) => !isWiringHub(getCatalogItem(light.ref)) && !wired.has(light.id),
		).length;
	const cableCount = Object.keys(wiringLayer.items).length;

	return (
		<div className="contents">
			<UIKit.Button
				type="button"
				className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-left text-sm hover:bg-bg"
				onClick={() => setAutoOpen(true)}
			>
				<UIKit.IconCable /> Автоматически создать проводку
			</UIKit.Button>
			{autoOpen && <WiringAutoDialog onClose={() => setAutoOpen(false)} />}
			<UIKit.Button
				type="button"
				title="Кабель (C)"
				className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm${tool === Tool.Cable ? " border-accent bg-accent-soft font-medium text-accent" : " border-border bg-surface text-ink hover:border-accent/50 hover:bg-bg"}`}
				onClick={(event) => {
					event.currentTarget.blur();
					setTool(Tool.Cable);
				}}
			>
				<UIKit.IconCable />
				Проложить кабель
			</UIKit.Button>
			<WiringGroups />
			{warnings.length > 0 && (
				<section className="flex flex-col gap-1 border-t border-border pt-2">
					<UIKit.Text className="text-xs font-semibold text-amber-700">
						Проверить: {warnings.length}
					</UIKit.Text>
					{warnings.slice(0, 20).map((warning) =>
						warning.cableIds.length === 1 ? (
							<UIKit.Button
								key={warning.message}
								type="button"
								className="text-left text-xs text-amber-700"
								onClick={() => setSelection({ kind: SelectionKind.Cable, id: warning.cableIds[0] })}
							>
								{warning.message}
							</UIKit.Button>
						) : (
							<div key={warning.message} className="flex flex-col gap-1">
								<UIKit.Text className="text-xs text-amber-700">{warning.message}</UIKit.Text>
								{warning.cableIds.map((id, index) => {
									const cable = layers.wiring.items[id];
									const material = cable?.material
										? cableItemById(cable.material)?.name
										: undefined;
									const group = cable?.groupId
										? layers.wiring.groups[cable.groupId]?.name
										: undefined;
									return (
										<UIKit.Button
											key={id}
											type="button"
											className="text-left text-xs text-amber-700 underline"
											onClick={() => setSelection({ kind: SelectionKind.Cable, id })}
										>
											{[`Кабель ${index + 1}`, material, group].filter(Boolean).join(" · ")}
										</UIKit.Button>
									);
								})}
							</div>
						),
					)}
					{warnings.length > 20 && (
						<UIKit.Text className="text-xs text-amber-700">
							Показаны первые 20 предупреждений.
						</UIKit.Text>
					)}
				</section>
			)}
			<div className="flex flex-col gap-1 border-t border-border pt-2.5">
				<UIKit.Text as="span" className="text-xs font-semibold tracking-label text-ink uppercase">
					Марка нового кабеля
				</UIKit.Text>
				<UIKit.Button
					type="button"
					className={`rounded-md border px-2 py-1.5 text-left text-xs ${cableMaterial === null ? "border-accent bg-accent-soft text-accent" : "border-transparent text-ink hover:bg-accent-soft"}`}
					onClick={() => setCableMaterial(null)}
				>
					Не указана
				</UIKit.Button>
				{CATALOG.cable.items.map((item) => (
					<UIKit.Button
						key={item.id}
						type="button"
						title={item.description}
						className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs ${cableMaterial === item.id ? "border-accent bg-accent-soft text-accent" : "border-transparent text-ink hover:bg-accent-soft"}`}
						onClick={() => setCableMaterial(item.id)}
					>
						<UIKit.Text
							as="span"
							className="size-2.5 shrink-0 rounded-sm"
							style={{ background: item.color }}
						/>
						{item.name}
					</UIKit.Button>
				))}
			</div>
			{panels.length === 0 && (
				<UIKit.Button
					type="button"
					className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-left text-xs text-amber-800 hover:bg-amber-50"
					onClick={() => {
						setTool(Tool.Place);
						setPlacing({ catalog: CatalogSectionKind.Electrical, item: ElectricalId.Panel });
					}}
				>
					<UIKit.IconSocket />
					Поставьте электрощит — от него идут группы питания
				</UIKit.Button>
			)}
			<div className="flex flex-col gap-1 rounded-lg bg-bg px-2.5 py-2 text-xs text-ink">
				<UIKit.Text as="span">
					Кабелей: <b className="text-ink">{cableCount}</b>
				</UIKit.Text>
				<UIKit.Text as="span">
					Распредкоробок: <b className="text-ink">{boxes.length}</b>
				</UIKit.Text>
				<UIKit.Text as="span">
					Приборов без кабеля:{" "}
					<b className={unconnected > 0 ? "text-amber-700" : "text-ink"}>{unconnected}</b>
				</UIKit.Text>
			</div>
			<UIKit.Text className="mt-auto text-xs text-ink">
				Кабель начинается кликом по розетке, выключателю, светильнику, распредкоробке или щитку и
				идёт по потолку только под прямыми углами: клики по плану ставят повороты, клик по другому
				прибору подключает. Спуски от приборов к потолку считаются сами. Связи выключателя со
				светильниками можно задать отдельно. Клик по кондиционеру ставит рядом вывод под него - от
				вывода кабель ведут до щитка. Alt — без привязки к сетке, Esc — отменить.
			</UIKit.Text>
		</div>
	);
};
